import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Lock, Image, Megaphone, Upload, User, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Background images state
  const [bgSettings, setBgSettings] = useState({
    landing_bg_engineering: '',
    landing_bg_originals: '',
    engineering_home_bg: '',
    originals_home_bg: '',
    custom_banner_text: '',
    originals_about_image: '',
    originals_about_text: '',
    og_image: ''
  });
  const [isSavingBg, setIsSavingBg] = useState(false);
  const [isSavingAbout, setIsSavingAbout] = useState(false);
  const [isSavingOg, setIsSavingOg] = useState(false);
  const [aboutImageFile, setAboutImageFile] = useState(null);
  const [aboutImagePreview, setAboutImagePreview] = useState('');
  const [ogImageFile, setOgImageFile] = useState(null);
  const [ogImagePreview, setOgImagePreview] = useState('');
  const aboutImageRef = useRef(null);
  const ogImageRef = useRef(null);

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      setBgSettings(res.data);
      if (res.data.originals_about_image) {
        setAboutImagePreview(res.data.originals_about_image);
      }
      if (res.data.og_image) {
        setOgImagePreview(res.data.og_image);
      }
    } catch (e) {
    }
  };

  const handleAboutImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image too large. Maximum 10MB allowed.');
        return;
      }
      setAboutImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAboutImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAboutSave = async () => {
    setIsSavingAbout(true);
    try {
      let imageUrl = bgSettings.originals_about_image || '';

      if (aboutImageFile) {
        const formData = new FormData();
        formData.append('file', aboutImageFile);
        const uploadRes = await axios.post(`${API}/upload-product-image`, formData);
        imageUrl = uploadRes.data.url;
      }

      await axios.put(`${API}/admin/site-settings`, {
        originals_about_image: imageUrl,
        originals_about_text: bgSettings.originals_about_text
      });

      setBgSettings(prev => ({ ...prev, originals_about_image: imageUrl }));
      setAboutImageFile(null);
      toast.success('About page saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setIsSavingAbout(false);
    }
  };

  const handleOgImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image too large. Maximum 10MB allowed.');
        return;
      }
      setOgImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setOgImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleOgSave = async () => {
    setIsSavingOg(true);
    try {
      let imageUrl = bgSettings.og_image || '';

      if (ogImageFile) {
        const formData = new FormData();
        formData.append('file', ogImageFile);
        const uploadRes = await axios.post(`${API}/upload-product-image`, formData);
        imageUrl = uploadRes.data.url;
      }

      await axios.put(`${API}/admin/site-settings`, { og_image: imageUrl });
      setBgSettings(prev => ({ ...prev, og_image: imageUrl }));
      setOgImageFile(null);
      toast.success('Share image saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setIsSavingOg(false);
    }
  };

  const handleBgSave = async () => {
    setIsSavingBg(true);
    try {
      await axios.put(`${API}/admin/site-settings`, bgSettings);
      toast.success('Settings saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setIsSavingBg(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsChangingPassword(true);
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      toast.success('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-8 max-w-xl">
      <div>
        <h2 className="text-2xl font-bold text-white">Settings</h2>
        <p className="text-gray-400 text-sm mt-1">Manage your admin account settings</p>
      </div>

      {/* Change Password */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#F54021]/20 rounded-lg">
            <Lock className="w-5 h-5 text-[#F54021]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Change Password</h3>
            <p className="text-gray-400 text-sm">Update your admin password</p>
          </div>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Current Password</Label>
            <Input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300">New Password</Label>
            <Input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300">Confirm New Password</Label>
            <Input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={isChangingPassword}
            className="w-full bg-[#F54021] hover:bg-[#ff5a35]"
          >
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </form>
      </div>

      {/* Banner Text */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#d4af37]/20 rounded-lg">
            <Megaphone className="w-5 h-5 text-[#d4af37]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Shop Banner</h3>
            <p className="text-gray-400 text-sm">Custom text that appears in the scrolling banner</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Banner Text</Label>
            <Input
              value={bgSettings.custom_banner_text || ''}
              onChange={(e) => setBgSettings({ ...bgSettings, custom_banner_text: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder="e.g. Free UK Shipping, Happy Christmas, etc."
            />
            <p className="text-gray-500 text-xs">Leave blank to only show discount codes in the banner. This text appears alongside any discount codes you've marked to show in the banner.</p>
          </div>

          <Button
            onClick={handleBgSave}
            disabled={isSavingBg}
            className="w-full bg-[#d4af37] hover:bg-[#c9a432] text-black"
          >
            {isSavingBg ? 'Saving...' : 'Save Banner Text'}
          </Button>
        </div>
      </div>

      {/* Originals About Page */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#d4af37]/20 rounded-lg">
            <User className="w-5 h-5 text-[#d4af37]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Originals About Page</h3>
            <p className="text-gray-400 text-sm">Upload a photo and write about yourself</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Photo</Label>
            <div
              onClick={() => aboutImageRef.current?.click()}
              className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-[#d4af37] transition-colors"
            >
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-400 text-sm">Click to upload photo</p>
            </div>
            <input
              ref={aboutImageRef}
              type="file"
              accept="image/*"
              onChange={handleAboutImageSelect}
              className="hidden"
            />
            {aboutImagePreview && (
              <div className="relative">
                <img src={aboutImagePreview} alt="About preview" className="w-full h-48 object-cover rounded-lg" />
                {aboutImageFile && <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">New image selected</span>}
                <button
                  type="button"
                  onClick={() => { setAboutImagePreview(''); setAboutImageFile(null); setBgSettings(prev => ({ ...prev, originals_about_image: '' })); }}
                  className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">About Text</Label>
            <Textarea
              value={bgSettings.originals_about_text || ''}
              onChange={(e) => setBgSettings({ ...bgSettings, originals_about_text: e.target.value })}
              rows={6}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder="Write about yourself and your work..."
            />
            <p className="text-gray-500 text-xs">Use blank lines to separate paragraphs.</p>
          </div>

          <Button
            onClick={handleAboutSave}
            disabled={isSavingAbout}
            className="w-full bg-[#d4af37] hover:bg-[#c9a432] text-black"
          >
            {isSavingAbout ? 'Saving...' : 'Save About Page'}
          </Button>
        </div>
      </div>

      {/* Social Share Image (OG Image) */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Megaphone className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Social Share Image</h3>
            <p className="text-gray-400 text-sm">This image appears when your website is shared on Facebook, Twitter, etc.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div
            onClick={() => ogImageRef.current?.click()}
            className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-400 text-sm">Click to upload share image</p>
            <p className="text-gray-500 text-xs mt-1">Recommended: 1200x630px</p>
          </div>
          <input
            ref={ogImageRef}
            type="file"
            accept="image/*"
            onChange={handleOgImageSelect}
            className="hidden"
          />
          {ogImagePreview && (
            <div className="relative">
              <img src={ogImagePreview} alt="Social share preview" className="w-full h-48 object-cover rounded-lg" />
              {ogImageFile && <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">New image selected</span>}
            </div>
          )}
          <Button
            onClick={handleOgSave}
            disabled={isSavingOg}
            className="w-full bg-blue-500 hover:bg-blue-600"
          >
            {isSavingOg ? 'Saving...' : 'Save Share Image'}
          </Button>
        </div>
      </div>

      {/* Background Images */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-[#d4af37]/20 rounded-lg">
            <Image className="w-5 h-5 text-[#d4af37]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Background Images</h3>
            <p className="text-gray-400 text-sm">Customize the background images for each page</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Landing Page - Engineering Side</Label>
            <Input
              value={bgSettings.landing_bg_engineering}
              onChange={(e) => setBgSettings({ ...bgSettings, landing_bg_engineering: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="Image URL"
            />
            {bgSettings.landing_bg_engineering && (
              <img src={bgSettings.landing_bg_engineering} alt="Preview" className="w-full h-24 object-cover rounded mt-2" />
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300">Landing Page - Originals Side</Label>
            <Input
              value={bgSettings.landing_bg_originals}
              onChange={(e) => setBgSettings({ ...bgSettings, landing_bg_originals: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="Image URL"
            />
            {bgSettings.landing_bg_originals && (
              <img src={bgSettings.landing_bg_originals} alt="Preview" className="w-full h-24 object-cover rounded mt-2" />
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300">Engineering Home Page</Label>
            <Input
              value={bgSettings.engineering_home_bg}
              onChange={(e) => setBgSettings({ ...bgSettings, engineering_home_bg: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="Image URL"
            />
            {bgSettings.engineering_home_bg && (
              <img src={bgSettings.engineering_home_bg} alt="Preview" className="w-full h-24 object-cover rounded mt-2" />
            )}
          </div>
          
          <div className="space-y-2">
            <Label className="text-gray-300">Originals Home Page</Label>
            <Input
              value={bgSettings.originals_home_bg}
              onChange={(e) => setBgSettings({ ...bgSettings, originals_home_bg: e.target.value })}
              className="bg-gray-700 border-gray-600 text-white text-sm"
              placeholder="Image URL"
            />
            {bgSettings.originals_home_bg && (
              <img src={bgSettings.originals_home_bg} alt="Preview" className="w-full h-24 object-cover rounded mt-2" />
            )}
          </div>

          <Button
            onClick={handleBgSave}
            disabled={isSavingBg}
            className="w-full bg-[#d4af37] hover:bg-[#c9a432] text-black"
          >
            {isSavingBg ? 'Saving...' : 'Save Background Images'}
          </Button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Admin Panel Information</h3>
        <div className="space-y-3 text-gray-400 text-sm">
          <p>• The default admin password is <code className="bg-gray-700 px-2 py-0.5 rounded">admin123</code></p>
          <p>• Change your password after first login for security</p>
          <p>• Discount codes can only be used once per customer email</p>
          <p>• Banner messages from discount codes will appear on the shop page</p>
        </div>
      </div>
    </div>
  );
}
